import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import { bookingService } from '../../../services/bookingService';
import { quoteRequestService } from '../../../services/quoteRequestService';
import QuoteRequestForm from '../../../components/quote/QuoteRequestForm';
import { useAuth } from '../../../contexts/AuthContext';

const BookingModal = ({ isOpen, onClose, vehicle, driver, onComplete }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    customerPhone: '',
    startDate: '',
    endDate: '',
    pickupLocation: '',
    dropoffLocation: '',
    notes: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [showQuoteRequest, setShowQuoteRequest] = useState(false);
  const [quoteRequestData, setQuoteRequestData] = useState(null);

  const calculateCosts = () => {
    if (!formData?.startDate || !formData?.endDate) {
      return { totalDays: 0, vehicleCost: 0, driverCost: 0, totalCost: 0 };
    }

    const start = new Date(formData?.startDate);
    const end = new Date(formData?.endDate);
    const diffTime = Math.abs(end - start);
    const totalDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

    const vehicleCost = (vehicle?.dailyRate || 0) * totalDays;
    const driverCost = driver ? (driver?.dailyRate || 0) * totalDays : 0;
    const totalCost = vehicleCost + driverCost;

    return { totalDays, vehicleCost, driverCost, totalCost };
  };

  const costs = calculateCosts();

  const handleSubmit = async (e) => {
    e?.preventDefault();

    if (!formData?.customerName || !formData?.customerEmail || !formData?.customerPhone) {
      setError('Veuillez remplir tous les champs obligatoires');
      return;
    }

    if (!formData?.startDate || !formData?.endDate) {
      setError('Veuillez sélectionner les dates de location');
      return;
    }

    if (!formData?.pickupLocation) {
      setError('Veuillez indiquer le lieu de prise en charge');
      return;
    }

    try {
      setLoading(true);
      setError('');

      const booking = {
        vehicleId: vehicle?.id,
        driverId: driver?.id || null,
        customerName: formData?.customerName,
        customerEmail: formData?.customerEmail,
        customerPhone: formData?.customerPhone,
        startDate: formData?.startDate,
        endDate: formData?.endDate,
        pickupLocation: formData?.pickupLocation,
        dropoffLocation: formData?.dropoffLocation || formData?.pickupLocation,
        withDriver: !!driver,
        totalDays: costs?.totalDays,
        vehicleCost: costs?.vehicleCost,
        driverCost: costs?.driverCost,
        totalCost: costs?.totalCost,
        status: 'quote_requested',
        notes: formData?.notes
      };

      const createdBooking = await bookingService?.create(booking);

      // Create quote request
      const quoteRequest = {
        requestType: 'vehicle_booking',
        referenceId: createdBooking?.id,
        customerName: formData?.customerName,
        customerEmail: formData?.customerEmail,
        customerPhone: formData?.customerPhone,
        requestDetails: {
          vehicleId: vehicle?.id,
          vehicleName: vehicle?.name,
          vehicleBrand: vehicle?.brand,
          vehicleModel: vehicle?.model,
          driverId: driver?.id,
          driverName: driver?.fullName,
          startDate: formData?.startDate,
          endDate: formData?.endDate,
          totalDays: costs?.totalDays,
          pickupLocation: formData?.pickupLocation,
          dropoffLocation: formData?.dropoffLocation || formData?.pickupLocation,
          withDriver: !!driver,
          notes: formData?.notes
        },
        subtotal: costs?.totalCost,
        totalAmount: costs?.totalCost,
        currency: 'CDF'
      };

      const createdQuoteRequest = await quoteRequestService?.createQuoteRequest(quoteRequest);

      setQuoteRequestData({
        quoteNumber: createdQuoteRequest?.quoteNumber,
        serviceType: 'Location de Véhicule',
        itemName: `${vehicle?.brand} ${vehicle?.model}${driver ? ' + Chauffeur' : ''}`,
        duration: `${costs?.totalDays} jour${costs?.totalDays > 1 ? 's' : ''}`,
        totalAmount: `${costs?.totalCost?.toLocaleString()} CDF`
      });

      setShowQuoteRequest(true);
    } catch (err) {
      setError(err?.message || 'Erreur lors de la réservation');
    } finally {
      setLoading(false);
    }
  };

  const handleQuoteRequestSuccess = (result) => {
    setSuccess(true);
    setTimeout(() => {
      onComplete();
    }, 2000);
  };

  const handleQuoteRequestError = (error) => {
    setError(error?.message || 'Erreur lors de l\'envoi de la demande');
    setShowQuoteRequest(false);
  };

  if (!isOpen) return null;

  if (success) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
        <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-md p-8 text-center">
          <div className="w-16 h-16 bg-green-500/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="CheckCircle2" size={40} className="text-green-400" />
          </div>
          <h2 className="text-2xl font-bold text-white mb-2">Réservation Confirmée!</h2>
          <p className="text-slate-400 mb-6">
            Votre demande de réservation a été envoyée avec succès. Nous vous contacterons bientôt.
          </p>
          <Button variant="default" fullWidth onClick={onComplete}>
            Fermer
          </Button>
        </div>
      </div>
    );
  }

  if (showQuoteRequest && quoteRequestData) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
        <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-2xl my-8">
          <div className="flex items-center justify-between p-6 border-b border-white/10">
            <div>
              <h2 className="text-2xl font-bold text-white">Confirmation de Demande</h2>
              <p className="text-sm text-slate-400 mt-1">
                Référence : {quoteRequestData?.quoteNumber}
              </p>
            </div>
            <button
              onClick={() => setShowQuoteRequest(false)}
              className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
            >
              <Icon name="X" size={24} />
            </button>
          </div>

          <div className="p-6">
            <QuoteRequestForm
              requestData={quoteRequestData}
              onSuccess={handleQuoteRequestSuccess}
              onError={handleQuoteRequestError}
              confirmButtonText="Confirmer la Demande"
            />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm overflow-y-auto">
      <div className="bg-slate-900 border border-white/10 rounded-3xl shadow-2xl w-full max-w-4xl my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10">
          <div>
            <h2 className="text-2xl font-bold text-white">Finaliser la Réservation</h2>
            <p className="text-sm text-slate-400 mt-1">
              Remplissez vos informations pour confirmer la réservation
            </p>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-full hover:bg-slate-800 flex items-center justify-center transition-colors"
          >
            <Icon name="X" size={24} />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left Column - Booking Details */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Détails de la Réservation</h3>

                {/* Vehicle Info */}
                <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                  <div className="flex items-center gap-4">
                    <Image
                      src={vehicle?.image}
                      alt={vehicle?.imageAlt}
                      className="w-20 h-14 rounded object-cover"
                    />
                    <div>
                      <h4 className="font-semibold text-white">{vehicle?.name}</h4>
                      <p className="text-sm text-slate-400">
                        {vehicle?.dailyRate?.toLocaleString()} CDF / jour
                      </p>
                    </div>
                  </div>
                </div>

                {/* Driver Info */}
                {driver && (
                  <div className="bg-slate-800/50 rounded-lg p-4 mb-4">
                    <div className="flex items-center gap-4">
                      <Image
                        src={driver?.avatar || 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=400'}
                        alt={driver?.avatarAlt || `Photo de ${driver?.fullName}`}
                        className="w-12 h-12 rounded-full object-cover"
                      />
                      <div>
                        <h4 className="font-semibold text-white">{driver?.fullName}</h4>
                        <p className="text-sm text-slate-400">
                          {driver?.dailyRate?.toLocaleString()} CDF / jour
                        </p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Date Selection */}
                <div className="space-y-4">
                  <Input
                    type="date"
                    label="Date de début"
                    required
                    value={formData?.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e?.target?.value })}
                    min={new Date()?.toISOString()?.split('T')?.[0]}
                  />
                  <Input
                    type="date"
                    label="Date de fin"
                    required
                    value={formData?.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e?.target?.value })}
                    min={formData?.startDate || new Date()?.toISOString()?.split('T')?.[0]}
                  />
                  <Input
                    type="text"
                    label="Lieu de prise en charge"
                    required
                    placeholder="Ex: Aéroport de N'djili"
                    value={formData?.pickupLocation}
                    onChange={(e) => setFormData({ ...formData, pickupLocation: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Lieu de restitution (optionnel)"
                    placeholder="Laisser vide si identique"
                    value={formData?.dropoffLocation}
                    onChange={(e) => setFormData({ ...formData, dropoffLocation: e?.target?.value })}
                  />
                </div>
              </div>
            </div>

            {/* Right Column - Customer Info & Summary */}
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-semibold text-white mb-4">Vos Informations</h3>
                <div className="space-y-4">
                  <Input
                    type="text"
                    label="Nom complet"
                    required
                    placeholder="Votre nom"
                    value={formData?.customerName}
                    onChange={(e) => setFormData({ ...formData, customerName: e?.target?.value })}
                  />
                  <Input
                    type="email"
                    label="Email"
                    required
                    placeholder="votre@email.com"
                    value={formData?.customerEmail}
                    onChange={(e) => setFormData({ ...formData, customerEmail: e?.target?.value })}
                  />
                  <Input
                    type="tel"
                    label="Téléphone"
                    required
                    placeholder=""
                    value={formData?.customerPhone}
                    onChange={(e) => setFormData({ ...formData, customerPhone: e?.target?.value })}
                  />
                  <Input
                    type="text"
                    label="Notes (optionnel)"
                    placeholder="Informations supplémentaires"
                    value={formData?.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e?.target?.value })}
                  />
                </div>
              </div>

              {/* Cost Summary */}
              <div className="bg-slate-800/50 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-white mb-4">Résumé des Coûts</h3>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-slate-400">Véhicule ({costs?.totalDays} jours)</span>
                    <span className="font-medium text-white">{costs?.vehicleCost?.toLocaleString()} CDF</span>
                  </div>
                  {driver && (
                    <div className="flex justify-between text-sm">
                      <span className="text-slate-400">Chauffeur ({costs?.totalDays} jours)</span>
                      <span className="font-medium text-white">{costs?.driverCost?.toLocaleString()} CDF</span>
                    </div>
                  )}
                  <div className="border-t border-white/10 pt-2 mt-2">
                    <div className="flex justify-between">
                      <span className="font-semibold text-white">Total</span>
                      <span className="text-xl font-bold text-blue-400">{costs?.totalCost?.toLocaleString()} CDF</span>
                    </div>
                    <p className="text-xs text-slate-400 mt-1 text-right">
                      ≈ ${(costs?.totalCost / 2500)?.toFixed(2)} USD
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="px-6 pb-4">
              <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <Icon name="AlertCircle" size={20} className="text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-red-400 text-sm">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="flex gap-3 p-6 border-t border-white/10">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              disabled={loading}
              className="flex-1"
            >
              Annuler
            </Button>
            <Button
              type="submit"
              onClick={handleSubmit}
              disabled={loading || !costs?.totalCost}
              iconName={loading ? "Loader2" : "FileText"}
              iconClassName={loading ? "animate-spin" : ""}
              iconPosition="left"
              className="flex-1"
            >
              {loading ? 'Traitement...' : 'Demander un Devis Gratuit'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default BookingModal;
